import React from "react";
import { about, business, mapDirections } from "../data/site";
import { ArrowUpRight, Pin, Star } from "./Icons";
import Reveal from "./Reveal";
import SectionLabel from "./SectionLabel";
import { cx, display, eyebrow, inner } from "../lib/ui";

const headlineIndent = [
  "",
  "italic pl-[clamp(0px,8vw,150px)]",
  "pl-[clamp(0px,3vw,60px)]",
];

export default function About() {
  return (
    <section id="about" className="relative scroll-mt-nav bg-paper py-section text-ink">
      <div className={cx(inner, "px-gutter")}>
        {/* ---------- heading ---------- */}
        <header className="grid gap-[clamp(24px,4vw,60px)] border-b border-black/10 pb-[clamp(40px,6vw,78px)] md:grid-cols-[200px_1fr] md:items-start">
          <SectionLabel>{about.eyebrow}</SectionLabel>

          <h2 className={cx(display, "text-[clamp(40px,7.6vw,108px)]")}>
            {about.headline.map((word, i) => (
              <Reveal
                as="span"
                mask
                key={word}
                delay={i * 90}
                className={cx("block", headlineIndent[i])}
              >
                {word}
              </Reveal>
            ))}
          </h2>
        </header>

        {/* ---------- office photograph + copy ---------- */}
        <div className="grid gap-[clamp(34px,5vw,82px)] pt-[clamp(40px,6vw,78px)] md:grid-cols-[1.05fr_1fr] md:items-start">
          <Reveal className="group">
            <figure className="m-0">
              <div className="overflow-hidden bg-paper-50">
                <img
                  src={about.officeImage}
                  alt="The Shree Siddhi Realty office building in Thaltej, Ahmedabad"
                  loading="lazy"
                  width="1200"
                  height="800"
                  className="h-[clamp(300px,52vw,620px)] w-full scale-[1.02] object-cover grayscale contrast-[1.05] transition-[transform,filter] duration-[1100ms] ease-out group-hover:scale-[1.07] group-hover:contrast-100 group-hover:grayscale-0"
                />
              </div>
              <figcaption className="mt-3.5 text-xs tracking-[0.04em] text-ash-400">
                {about.officeCaption}
              </figcaption>
            </figure>
          </Reveal>

          <div className="grid content-start gap-5">
            <Reveal
              className="inline-flex items-center gap-2.5 self-start rounded-full border border-black/10 py-2 pl-3 pr-4 text-xs text-ash-400"
              delay={80}
            >
              <span className="inline-flex gap-0.5 text-ink" aria-hidden="true">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} width="13" height="13" />
                ))}
              </span>
              <strong className="font-display text-[17px] font-normal leading-none text-ink">
                {business.rating}
              </strong>
              <span>
                {business.reviewCount} {business.reviewLabel}
              </span>
            </Reveal>

            <Reveal delay={140}>
              <p className="font-display text-[clamp(19px,2.3vw,28px)] leading-[1.3] text-ink">
                {business.role}
              </p>
            </Reveal>

            {about.body.map((paragraph, i) => (
              <Reveal key={i} delay={200 + i * 90}>
                <p className="max-w-[60ch] text-[clamp(14.5px,1.15vw,16.5px)] leading-[1.75] text-ink-500">
                  {paragraph}
                </p>
              </Reveal>
            ))}

            <Reveal
              className="mt-1.5 grid grid-cols-[auto_1fr] items-start gap-x-3.5 gap-y-1.5 border-t border-black/10 pt-6"
              delay={380}
            >
              <Pin width="18" height="18" className="mt-1 text-ink" />
              <address className="grid gap-0.5 text-[14.5px] not-italic text-ink-600">
                {business.address.lines.map((line) => (
                  <span key={line}>{line}</span>
                ))}
              </address>
              <a
                href={mapDirections}
                target="_blank"
                rel="noreferrer noopener"
                className="col-start-2 mt-2 inline-flex items-center gap-1.5 justify-self-start border-b border-ink pb-[3px] text-[11px] font-medium uppercase tracking-[0.18em] transition-[gap,opacity] duration-[350ms] ease-out hover:gap-3 hover:opacity-60"
              >
                Directions
                <ArrowUpRight width="13" height="13" />
              </a>
            </Reveal>

            <ul className="mt-3 grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-[22px] border-t border-black/10 pt-7">
              {about.stats.map((stat, i) => (
                <Reveal as="li" key={stat.label} delay={440 + i * 90} className="grid gap-1.5">
                  <strong
                    className={cx(
                      display,
                      "inline-flex items-start gap-[3px] text-[clamp(28px,3.4vw,44px)] font-normal leading-none"
                    )}
                  >
                    {stat.value}
                    <em className="mt-[0.08em] text-[0.55em] not-italic">
                      {stat.suffix}
                    </em>
                  </strong>
                  <span className={cx(eyebrow, "tracking-[0.16em]")}>{stat.label}</span>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>

        {/* ---------- team photograph (frame held until the real shot lands) ---------- */}
        <Reveal className="mt-[clamp(48px,7vw,104px)] grid gap-[22px] md:grid-cols-[1fr_300px] md:items-end md:gap-[clamp(28px,4vw,64px)]">
          <div className="overflow-hidden bg-paper-50">
            {about.teamImage ? (
              <img
                src={about.teamImage}
                alt={`The team at ${business.name}`}
                loading="lazy"
                className="h-[clamp(260px,44vw,560px)] w-full object-cover grayscale"
              />
            ) : (
              <div
                role="img"
                aria-label="Team photo coming soon"
                className="grid h-[clamp(240px,38vw,460px)] place-content-center justify-items-center gap-3.5 border border-dashed border-black/30 bg-hatch p-8 text-center"
              >
                <span className="grid h-[54px] w-[54px] place-content-center gap-1.5 border border-black/35">
                  <i className="-ml-1.5 block h-[7px] w-4 -skew-x-[18deg] bg-ink" />
                  <i className="ml-1.5 block h-[7px] w-4 -skew-x-[18deg] bg-ink" />
                </span>
                <p className={eyebrow}>Team photo</p>
                <p className="max-w-[34ch] text-[12.5px] text-ash-400">
                  Reserved — drop the photograph in and it fills this frame.
                </p>
              </div>
            )}
          </div>

          <div className="grid gap-2.5 font-display text-[clamp(19px,2.2vw,26px)] text-ink-600 md:pb-2">
            <SectionLabel>Our People</SectionLabel>
            <p>{about.teamCaption}</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
